import { useEffect, useId, useRef } from "react";
import { signalFor } from "../../data/telemetry";
import { labelOf, labelOfId } from "../../data/decomposition";

// Friendly names for the link keys we know about. A key that is not here is
// NOT dropped: it falls back to its own name, upper-cased.
export const LINK_LABELS = {
  repo: "SOURCE", live: "LIVE", doi: "DOI", paper: "PAPER", dist: "PACKAGE",
  page: "SHEET", docs: "DOCS", video: "VIDEO", slides: "SLIDES",
};

// Known fields, rendered in this order whenever the node carries them.
const KNOWN_FIELDS = [
  ["note", "NOTE"], ["summary", "SUMMARY"], ["started", "STARTED"],
  ["stack", "STACK"], ["people", "PEOPLE"], ["awards", "AWARDS"],
];

// Keys with their own dedicated row, rendered explicitly rather than by the
// generic pass. Everything else on a node is rendered; see STRUCTURAL / ENGINE
// below for the only exclusions.
const HANDLED = new Set([
  "id", "label", "role", "kind", "parent", "unknown", "priv", "links",
  ...KNOWN_FIELDS.map(([k]) => k),
]);
// Structural data the panel deliberately does not surface (layout hints that
// have no meaning to a reader). This is a decision, not engine noise.
const STRUCTURAL = new Set(["r", "standalone"]);
// Keys the graph engine actually writes onto node objects. Nothing else is
// hidden: if a later task writes more onto nodes, strip it at the source in
// graph.js rather than adding to this list.
const ENGINE = new Set(["x", "y", "z", "vx", "vy", "vz", "fx", "fy", "fz", "index"]);

// Only "nothing there" is empty. false and 0 are real content.
const isEmpty = (v) =>
  v === undefined || v === null || v === "" ||
  (Array.isArray(v) && v.every(isEmpty)) ||
  (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

const safeHref = (u) =>
  typeof u === "string" && (/^https?:\/\//i.test(u) || u.startsWith("/")) ? u : null;

const isInternal = (u) => typeof u === "string" && u.startsWith("/");

const prettyUrl = (u) => u.replace(/^https?:\/\//i, "").replace(/\/$/, "");

const humanKey = (k) =>
  k.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").toUpperCase();

function Anchor({ href, children }) {
  const ok = safeHref(href);
  if (!ok) return <>{children}</>;
  return isInternal(ok)
    ? <a href={ok}>{children}</a>
    : <a href={ok} target="_blank" rel="noreferrer">{children}</a>;
}

// Renders any value a node might carry: scalars as text, arrays as lists,
// objects (people and anything shaped like them) as name + role + link or
// key/value pairs. No value type is silently skipped.
function Value({ v }) {
  if (isEmpty(v)) return null;
  if (Array.isArray(v)) {
    return (
      <ul className="d-list">
        {v.filter((x) => !isEmpty(x)).map((x, i) => <li key={i}><Value v={x} /></li>)}
      </ul>
    );
  }
  if (typeof v === "object") {
    if (typeof v.name === "string") {
      const { name, role, url, ...rest } = v;
      return (
        <span className="d-person">
          <Anchor href={url}>{name}</Anchor>
          {role ? <span className="d-role"> ({String(role)})</span> : null}
          {Object.entries(rest).filter(([, x]) => !isEmpty(x)).map(([k, x]) => (
            <span key={k} className="d-role"> {humanKey(k)}: <Value v={x} /></span>
          ))}
        </span>
      );
    }
    return (
      <span>
        {Object.entries(v).filter(([, x]) => !isEmpty(x)).map(([k, x], i) => (
          <span key={k}>{i ? "; " : ""}{humanKey(k)}: <Value v={x} /></span>
        ))}
      </span>
    );
  }
  const text = String(v);
  return safeHref(text) ? <Anchor href={text}>{prettyUrl(text)}</Anchor> : <>{text}</>;
}

function Row({ k, className, children }) {
  return (
    <div className="d-row">
      <dt>{k}</dt>
      <dd className={className}>{children}</dd>
    </div>
  );
}

function LinkRow({ linkKey, url }) {
  const label = LINK_LABELS[linkKey] || linkKey.toUpperCase();
  const href = safeHref(url);
  const text = isInternal(href) ? "open" : prettyUrl(String(url));
  return (
    <Row k={label}>
      {href ? (
        <Anchor href={href}>
          <span className="sr-only">{label}: </span>{text}<span aria-hidden="true"> ↗</span>
        </Anchor>
      ) : String(url)}
    </Row>
  );
}

// The node's destination: a deployed world (links.live) or one of this site's
// own sheets (links.page, e.g. the CV). Either gets the same ENTER button.
// (priv means closed SOURCE only; a closed-source product can still be live.)
function entranceKey(node) {
  if (!node.links) return null;
  if (safeHref(node.links.live)) return "live";
  if (safeHref(node.links.page)) return "page";
  return null;
}

function Entrance({ node }) {
  const key = entranceKey(node);
  const href = key ? safeHref(node.links[key]) : null;
  if (!href) return null;
  const external = !isInternal(href);
  return (
    <a
      className="d-enter"
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      <span>ENTER {String(labelOf(node)).toUpperCase()}</span>
      <span aria-hidden="true"> →</span>
      {external ? <span className="sr-only"> (opens in a new tab)</span> : null}
    </a>
  );
}

/**
 * Everything a node carries, as a definition list. The body is built by
 * iterating the node, never from a fixed list of fields, so nothing added to a
 * node can be silently lost.
 *
 * A node with a destination (links.live, else links.page) gets one prominent
 * ENTER link above the reference list; that url is not repeated as a row. `onEnter` is still accepted
 * for signature stability but unused: the entrance is a plain link.
 */
export default function DetailPanel({ node, onClose, onEnter = undefined, childCount = 0, isOpen = false }) {
  const ref = useRef(null);
  const headingId = useId();
  const present = !!node;

  // Remember who had focus when the panel appeared and give it back when the
  // panel goes away (closed, node cleared, or unmounted). Declared before the
  // focus-moving effect so the opener is captured first.
  useEffect(() => {
    if (!present) return undefined;
    const opener = document.activeElement;
    return () => {
      if (opener && opener !== document.body && document.contains(opener) && opener.focus) opener.focus();
    };
  }, [present]);

  // Move focus to the panel when a node is opened or changed, so keyboard and
  // screen-reader users land on what they just chose.
  useEffect(() => {
    if (node && ref.current) ref.current.focus();
  }, [node && node.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!node) return null;

  const sig = node.priv ? null : signalFor(node.id);
  const extras = Object.keys(node).filter(
    (k) => !HANDLED.has(k) && !STRUCTURAL.has(k) && !ENGINE.has(k) && !k.startsWith("__") && !isEmpty(node[k])
  );
  const links = Object.entries(node.links || {}).filter(
    ([k, u]) => !isEmpty(u) && k !== entranceKey(node) && !(node.priv && k === "repo") // closed source: no repo anchor, ever
  );

  return (
    <section
      ref={ref}
      tabIndex={-1}
      className="plate-detail"
      aria-labelledby={headingId}
      onKeyDown={(e) => { if (e.key === "Escape") onClose && onClose(); }}
    >
      <div className="d-head">
        <h2 className="d-name" id={headingId}>{labelOf(node)}</h2>
        <button type="button" className="d-close" onClick={onClose}>
          <span aria-hidden="true">✕</span><span className="sr-only">Close</span>
        </button>
      </div>
      {node.role ? <p className="d-role">{node.role}</p> : null}
      <Entrance node={node} />
      <dl className="d-body">
        <Row k="CLASS">{String(node.kind || "").toUpperCase()}</Row>
        {KNOWN_FIELDS.filter(([k]) => !isEmpty(node[k])).map(([k, label]) => (
          <Row key={k} k={label}><Value v={node[k]} /></Row>
        ))}
        {extras.map((k) => <Row key={k} k={humanKey(k)}><Value v={node[k]} /></Row>)}
        {childCount > 0 ? <Row k="CONTAINS">{childCount} · {isOpen ? "OPEN" : "CLOSED"}</Row> : null}
        {node.parent ? <Row k="INSIDE">{labelOfId(node.parent)}</Row> : null}
        {node.unknown ? <Row k="PLACEMENT" className="sig-DEGRADING">UNRESOLVED</Row> : null}
        {node.priv ? (
          <>
            <Row k="SOURCE">CLOSED</Row>
            <Row k="SIGNAL">NOT PUBLISHED</Row>
          </>
        ) : sig && sig.signal ? (
          <>
            <Row k="LAST PUSH">{sig.pushed}</Row>
            <Row k="AGE">{sig.ageDays} d</Row>
            <Row k="SIGNAL" className={`sig-${sig.signal}`}>{sig.signal}</Row>
            {sig.lang ? <Row k="LANGUAGE">{sig.lang}</Row> : null}
          </>
        ) : null}
        {links.map(([k, u]) => <LinkRow key={k} linkKey={k} url={u} />)}
      </dl>
    </section>
  );
}
